import React from 'react';
import { CircularProgress, Grow, Stack, Box, alpha, Button } from '@mui/material';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { Typography, LoadingButton, LoadingButtonProps, QRCode } from '@ocx/ui';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OfferIneligibilityWarning } from '@ocx/ui-offers';

import { MarketingContent } from '../../modules/offers/offer.types';
import { SizingMultiplier } from '../../theme/constants';
import { OfferDetailsTermsAndDisclaimer } from './OfferDetailsTermsAndDisclaimer';
import {
  OFFER_DETAILS_QR_CODE_SIZE,
  OFFER_DETAILS_MEDIA_IMAGE_SIZE,
  OFFER_DETAILS_QR_PADDING,
} from './offer-details.constants';
import { isOfferDisclaimerAvailable } from '../../modules/offers/marketing-content.utils';
import { SurgeonWarning } from '../SurgeonWarning/SurgeonWarning';
import { OfferDetailsAvailableAtLocation, OfferDetailsLocationsCarousel } from './OfferDetailsLocationsCarousel';
import { OfferLocationsCarouselItemOnClickParams } from './types';
import { DynamicCardContent, DynamicCardMedia } from '../DynamicCard';

export type OfferDetailsAction = {
  title: MessageDescriptor;
  buttonVariant?: LoadingButtonProps['variant'];
  isExecuting?: boolean;
  onClick: () => unknown;
};

export type OfferDetailsAutoIdentifyProps = {
  autoIdentify?: boolean;
  phoneNumber?: string;
  isLoading?: boolean;
  marketingContent: MarketingContent;
  action?: OfferDetailsAction | null;
  ineligibility?: {
    message: string;
  };
  availableAtLocations?: OfferDetailsAvailableAtLocation[];
  hasLocationConstraints?: boolean;
  memberHasSharedLocation?: boolean;
  isLocalOffersEnabled?: boolean;
  onStoreIconClick?: (params: OfferLocationsCarouselItemOnClickParams) => void;
  onClose: () => void;
};

export const OfferDetailsAutoIdentify: React.FC<OfferDetailsAutoIdentifyProps> = ({
  phoneNumber,
  isLoading = false,
  marketingContent,
  action,
  ineligibility,
  availableAtLocations,
  hasLocationConstraints,
  memberHasSharedLocation,
  isLocalOffersEnabled,
  onStoreIconClick,
  onClose,
}) => {
  const { title, imageUrl, disclaimer, disclaimerUrl, terms, termsUrl, surgeonGeneralsWarning } = marketingContent;
  const showTermsAndDisclaimerSection = isOfferDisclaimerAvailable(marketingContent);

  return (
    <Stack
      justifyContent="center"
      position="relative"
      overflow="hidden"
      p={SizingMultiplier.lg}
      gap={SizingMultiplier.lg}
      sx={(theme) => ({
        borderRadius: theme.spacing(SizingMultiplier.md, SizingMultiplier.md, 0, 0),
      })}>
      {surgeonGeneralsWarning && <SurgeonWarning square message={surgeonGeneralsWarning} size="large" />}

      <Stack spacing={SizingMultiplier.lg}>
        <Stack spacing={SizingMultiplier.sm}>
          <Typography variant="h5" fontWeight="bold">
            <FormattedMessage
              id="offer-details-qr:scan-at-register:title"
              defaultMessage="SCAN AT THE REGISTER"
              description="QR code section title"
            />
          </Typography>

          <Typography variant="body1">
            <FormattedMessage
              id="offer-details-qr:scan-at-register:helper-text"
              defaultMessage="If you have trouble scanning the code, please increase your screen brightness."
              description="Helper text for QR code scanning"
            />
          </Typography>
        </Stack>

        <Box
          sx={(theme) => ({
            p: theme.spacing(OFFER_DETAILS_QR_PADDING),
            borderRadius: SizingMultiplier.xl,
            border: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          })}>
          <QRCode text={phoneNumber || ''} canvasSize={OFFER_DETAILS_QR_CODE_SIZE} />
        </Box>

        <Box
          sx={{
            borderRadius: SizingMultiplier.xl,
            border: 1,
            borderColor: 'divider',
          }}>
          <DynamicCardContent sx={{ p: (theme) => theme.spacing(OFFER_DETAILS_QR_PADDING) }}>
            {imageUrl && (
              <DynamicCardMedia
                src={imageUrl}
                alt={title}
                sx={{
                  marginRight: SizingMultiplier.md,
                  height: OFFER_DETAILS_MEDIA_IMAGE_SIZE,
                  width: OFFER_DETAILS_MEDIA_IMAGE_SIZE,
                }}
              />
            )}
            <Stack flex={1} alignItems="flex-start" justifyContent="center">
              <Typography variant="subtitle1">{title}</Typography>
            </Stack>
          </DynamicCardContent>
        </Box>
      </Stack>

      <Stack spacing={SizingMultiplier.lg}>
        {isLocalOffersEnabled && (
          <OfferDetailsLocationsCarousel
            locations={availableAtLocations}
            hasLocationConstraints={hasLocationConstraints}
            memberHasSharedLocation={memberHasSharedLocation}
            onStoreIconClick={onStoreIconClick}
          />
        )}

        {showTermsAndDisclaimerSection && (
          <OfferDetailsTermsAndDisclaimer
            terms={terms}
            termsUrl={termsUrl}
            disclaimer={disclaimer}
            disclaimerUrl={disclaimerUrl}
          />
        )}

        {ineligibility && <OfferIneligibilityWarning message={ineligibility.message} />}

        <Grow in={!!action} appear unmountOnExit>
          <LoadingButton
            fullWidth
            variant={action?.buttonVariant}
            loading={action?.isExecuting || false}
            disabled={action?.isExecuting || isLoading || Boolean(ineligibility)}
            onClick={action?.onClick}>
            {!!action?.title && <FormattedMessage {...action.title} />}
          </LoadingButton>
        </Grow>

        <Button variant="text" onClick={onClose} sx={{ mt: SizingMultiplier.sm }}>
          <FormattedMessage
            id="offer-details-qr:close:button"
            defaultMessage="CLOSE"
            description="Close button for QR code modal"
          />
        </Button>
      </Stack>

      {isLoading && (
        <Stack
          position="absolute"
          height="100%"
          width="100%"
          justifyContent="center"
          alignItems="center"
          sx={(theme) => ({ backgroundColor: alpha(theme.palette.background.paper, 0.6) })}>
          <CircularProgress size={24} />
        </Stack>
      )}
    </Stack>
  );
};
